import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Package, 
  Truck, 
  Home,
  Clock,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${API_URL}/orders/track/${orderNumber}`);
      
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (err) {
      console.error('Fetch order error:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        label: 'Order Received',
        color: 'bg-blue-500',
        textColor: 'text-blue-700',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-500',
        icon: Package,
        message: 'We\'ve received your order and will start processing it soon.'
      },
      confirmed: {
        label: 'Order Confirmed',
        color: 'bg-green-500',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-500',
        icon: CheckCircle,
        message: 'Your order has been confirmed and is being prepared.'
      },
      processing: {
        label: 'Processing',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-500',
        icon: Clock,
        message: 'Your furniture is being prepared for delivery.'
      },
      out_for_delivery: {
        label: 'Out for Delivery',
        color: 'bg-purple-500',
        textColor: 'text-purple-700',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-500',
        icon: Truck,
        message: 'Your order is on its way! Our team will contact you shortly.'
      },
      delivered: {
        label: 'Delivered',
        color: 'bg-green-600',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-600',
        icon: Home,
        message: 'Your order has been successfully delivered. Thank you!'
      },
      cancelled: {
        label: 'Cancelled',
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-500',
        icon: Package,
        message: 'This order has been cancelled.'
      }
    };

    return statusMap[status] || statusMap.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-dark-600 font-semibold">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-dark-900 mb-2">Order Not Found</h2>
          <p className="text-dark-600 mb-6">
            We couldn't find an order with number: <strong>{orderNumber}</strong>
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 py-12">
      <div className="container mx-auto px-4">
        
        {/* Success Header */}
        <div className="max-w-4xl mx-auto">
          
          {/* Animated Success Icon */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block relative">
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-2xl animate-bounce-once">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <div className="absolute inset-0 w-24 h-24 bg-green-400 rounded-full animate-ping opacity-75" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-900 mt-6 mb-3">
              Order Confirmed!
            </h1>
            <p className="text-xl text-dark-600">
              Thank you for your order, {order.customer_name}
            </p>
          </div>

          {/* Order Number Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="text-center">
              <p className="text-sm text-dark-500 mb-2">Order Number</p>
              <p className="text-3xl font-bold text-primary-600 font-mono tracking-wider">
                {order.order_number}
              </p>
              <p className="text-sm text-dark-500 mt-4">
                Confirmation sent to: <strong>{order.customer_email}</strong>
              </p>
            </div>
          </div>

          {/* Status Card */}
          <div className={`${statusInfo.bgColor} border-2 ${statusInfo.borderColor} rounded-2xl p-6 mb-6`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${statusInfo.color} rounded-full flex items-center justify-center`}>
                <StatusIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold ${statusInfo.textColor}`}>
                  {statusInfo.label}
                </h3>
                <p className={`text-sm ${statusInfo.textColor}`}>
                  {statusInfo.message}
                </p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-2xl font-bold text-dark-900 mb-6 flex items-center gap-2">
              <Package className="w-6 h-6 text-primary-600" />
              Order Details
            </h2>

            {/* Items */}
            <div className="space-y-4 mb-6">
              {order.items && order.items.map((item, index) => (
                <div key={index} className="flex gap-4 pb-4 border-b last:border-b-0">
                  {item.product_image && (
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-dark-900 mb-2">
                      {item.product_name}
                    </h3>
                    
                    {/* Options Container */}
                    <div className="space-y-1 mb-2">
                      {/* Color Display */}
                      {item.color && (
                        <div className="flex items-center gap-1.5">
                          <div 
                            className="w-3 h-3 rounded-full border border-gray-300"
                            style={{ backgroundColor: item.color_hex || '#808080' }}
                          />
                          <span className="text-xs text-dark-600 font-medium">
                            Color: {item.color}
                          </span>
                        </div>
                      )}
                      
                      {/* Handle Display */}
                      {item.handle && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-blue-700 font-medium bg-blue-50 px-2 py-0.5 rounded">
                            Handle {item.handle}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-dark-500 mb-1">
                      Quantity: {item.quantity}
                    </p>
                    <p className="text-sm font-bold text-primary-600">
                      ${parseFloat(item.subtotal).toFixed(2)} CAD
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="space-y-3">
                <div className="flex justify-between text-dark-600">
                  <span>Subtotal:</span>
                  <span className="font-semibold">${parseFloat(order.subtotal).toFixed(2)} CAD</span>
                </div>
                <div className="flex justify-between text-dark-600">
                  <span>Delivery to {order.shipping_city}:</span>
                  <span className="font-semibold">${parseFloat(order.delivery_charge).toFixed(2)} CAD</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-dark-900 pt-3 border-t-2">
                  <span>Total:</span>
                  <span className="text-primary-600">${parseFloat(order.total).toFixed(2)} CAD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-2xl font-bold text-dark-900 mb-6 flex items-center gap-2">
              <Truck className="w-6 h-6 text-accent-600" />
              Delivery Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address */}
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-dark-900 mb-1">Delivery Address</p>
                  <p className="text-dark-600 leading-relaxed">
                    {order.shipping_address}<br />
                    {order.shipping_city}, {order.shipping_province}<br />
                    {order.shipping_postal_code}
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-dark-900 mb-1">Phone</p>
                    <a href={`tel:${order.customer_phone}`} className="text-primary-600 hover:underline">
                      {order.customer_phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-dark-900 mb-1">Email</p>
                    <a href={`mailto:${order.customer_email}`} className="text-primary-600 hover:underline break-all">
                      {order.customer_email}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Expected Delivery */}
            <div className="mt-6 bg-primary-50 border-2 border-primary-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-primary-600" />
                <div>
                  <p className="font-semibold text-primary-900">Expected Delivery</p>
                  <p className="text-primary-700">Within 2 working days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="text-4xl">💰</div>
              <div>
                <h3 className="font-bold text-dark-900 text-lg mb-2">
                  Cash on Delivery
                </h3>
                <p className="text-dark-700 mb-3">
                  Please have the exact amount ready when our delivery team arrives.
                </p>
                <div className="bg-white rounded-lg p-4 border-2 border-yellow-300">
                  <p className="text-sm text-dark-600 mb-1">Amount to Pay:</p>
                  <p className="text-3xl font-bold text-amber-600">
                    ${parseFloat(order.total).toFixed(2)} CAD
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 gap-4">
            <Link
              to="/products"
              className="flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors"
            >
              Continue Shopping
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

            {/* Help Section */}
           <div className="mt-8 text-center bg-white rounded-2xl shadow-lg p-6">
             <h3 className="font-bold text-dark-900 mb-2">Need Help?</h3>
             <p className="text-dark-600 mb-4">
               Our customer service team is here to assist you
             </p>
             <div className="flex flex-col sm:flex-row gap-4 justify-center">
               <a
                href="mailto:skyfurniturecanada@gmail.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
              >
                <Mail className="w-5 h-5" />
                skyfurniturecanada@gmail.com
              </a>
              <a
                href="tel:+16478726713"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
              >
                <Phone className="w-5 h-5" />
                +1 (647) 872-6713
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
        
        @keyframes bounce-once {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        
        .animate-bounce-once {
          animation: bounce-once 0.6s ease-in-out;
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OrderConfirmationPage;